'use client';

import { useParams, usePathname, useRouter } from 'next/navigation';
import Link from 'next/link';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Check, Search, Plus } from 'lucide-react';
import { AvatarWithFlair } from '@/components/prelaunch/avatar-with-flair';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { cn, gridHref } from '@/lib/utils';
import { generateGridAvatarUrl } from '@/lib/avatar';
import { useUserProfile } from '@/hooks';
import { calculateSummary, groupSeatsByCoin } from '@/components/seats';
import { CreatorGridCard, type CreatorGridData } from '@/components/profile/creator-grid-card';
import { ProfileTilesList } from '@/components/profile/profile-tiles-list';
import {
  useEthPrice,
  useWallet,
  useTokenPrices,
  effectiveTokenAddress,
  useTakeoverPrice,
  useLiveBalances,
  getLiveBalance,
} from '@/hooks';
import { HV_TAKEOVER_GRID_ID } from '@/lib/constants/addresses';
import { classicAppUrl } from '@/lib/classic-app';
import { useState, useMemo, useCallback } from 'react';
import { queryKeys } from '@/lib/query-keys';
import { fetchPortfolioJson } from '@/lib/fetchers/portfolio-json';
import { mergePortfolioPreservingCachedSeats } from '@/lib/drawer-utils';
import { ProfileHud } from '@/components/profile/profile-hud';
import { buildHudSnapshot } from '@/lib/profile/build-hud-snapshot';
import { useProfileModalOptional } from '@/contexts/profile-modal-context';
import { fetchWithTimeout } from '@/lib/fetch-with-timeout';

/** When `/api/portfolio/[address]` fails, still render a public profile from `/api/users` + zeros. */
function makeEmptyPortfolioUser(addr: string) {
  return {
    id: addr,
    createdAt: 0,
    v1TotalWithdrawn: '0',
    v1TotalDeposited: '0',
    v1TotalTaxesPaid: '0',
    v1TotalFeesEarned: '0',
    v1TotalCreatorFeesEarned: '0',
    v2TotalWithdrawnUsd: '0',
    v2TotalDepositedUsd: '0',
    v2TotalTaxesPaidUsd: '0',
    v2FeesEarnedUsd: '0',
    v2CreatorFeesEarnedUsd: '0',
  };
}

export interface ProfileClientProps {
  /** When set (e.g. modal), reads this address instead of the `[address]` route param. */
  addressOverride?: string;
  /** Rendered inside a dialog — keeps the same data as the full page. */
  embedded?: boolean;
  /** e.g. portfolio profile modal — close parent dialog */
  onClose?: () => void;
}

export default function ProfileClient({
  addressOverride,
  embedded = false,
  onClose,
}: ProfileClientProps = {}) {
  const params = useParams();
  const router = useRouter();
  const pathname = usePathname();
  const address = (addressOverride ?? (params?.address as string | undefined)) ?? '';

  const profileModalCtx = useProfileModalOptional();
  const closeProfileModal = useCallback(() => {
    if (onClose) onClose();
    else profileModalCtx?.closeProfile();
  }, [onClose, profileModalCtx]);

  const [copied, setCopied] = useState(false);
  const [avatarDialogOpen, setAvatarDialogOpen] = useState(false);

  const { address: connectedAddress, linkTwitter, user: privyUser } = useWallet();
  const isOwnProfile = connectedAddress?.toLowerCase() === address?.toLowerCase();
  const { ethPrice } = useEthPrice();
  const queryClient = useQueryClient();

  const addrLower = address.toLowerCase();

  const portfolioQuery = useQuery({
    queryKey: queryKeys.portfolio(address),
    queryFn: async () => {
      const fresh = await fetchPortfolioJson(address);
      // Preserve RPC-patched seats (newer `lastPriceChangeTime` /
      // `lastUpdateTime`) over a stale indexer payload so the post-tx
      // optimistic write from `updatePortfolioSeatsInCache` survives a
      // refetch landing during the indexer lag window.
      const cached = queryClient.getQueryData(queryKeys.portfolio(address));
      return mergePortfolioPreservingCachedSeats(cached, fresh);
    },
    enabled: !!address,
    staleTime: 30_000,
  });
  const profileData = portfolioQuery.data;
  const portfolioLoading = portfolioQuery.isPending;

  const { data: creatorGridsData, isLoading: isGridsLoading } = useQuery({
    queryKey: ['creator-grids', addrLower],
    queryFn: async () => {
      try {
        const res = await fetchWithTimeout(
          `/api/profile/${encodeURIComponent(addrLower)}/grids`,
          { timeoutMs: 60_000 },
        );
        if (!res.ok) return { data: [] };
        return res.json();
      } catch {
        return { data: [] };
      }
    },
    enabled: !!address,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const {
    profile: userProfile,
    avatarUrl: profileAvatarUrl,
    displayName: profileDisplayName,
    level: userLevel,
  } = useUserProfile(address);

  // The HUD's Socials row renders from the DB-backed profile, but the DB
  // record lands via `/api/verify-social` + a cache refresh — both of which
  // lag the actual OAuth link by seconds-to-minutes. For the viewer's own
  // profile, Privy is the source of truth and is current the moment the
  // link completes, so prefer it. Without this the row keeps offering
  // "Connect X" after a successful link, and re-clicking throws Privy's
  // "already has an account of type twitter linked" error.
  const hudUserProfile = useMemo(() => {
    if (userProfile?.twitter) return userProfile;
    if (isOwnProfile && privyUser?.twitter?.username) {
      return {
        ...(userProfile ?? {}),
        twitter: { username: privyUser.twitter.username },
      };
    }
    return userProfile ?? null;
  }, [userProfile, isOwnProfile, privyUser?.twitter?.username]);

  const portfolioRecord = profileData?.data;
  const user = portfolioRecord ?? makeEmptyPortfolioUser(address);
  const rawProfileSeats = portfolioRecord?.seats || [];
  const creatorGrids: CreatorGridData[] = creatorGridsData?.data || [];
  const liveAvgEarningsUsd = undefined;

  const liveSeatRefs = useMemo(() => {
    if (!isOwnProfile || rawProfileSeats.length === 0) return undefined;
    return rawProfileSeats
      .filter((s: { pool?: { managerAddress?: string } }) => !!s.pool?.managerAddress)
      .map((s: { pool?: { id?: string; managerAddress?: string }; poolId?: string; seatIndex: number }) => ({
        poolId: s.pool?.id || s.poolId || '',
        seatIndex: s.seatIndex,
        managerAddress: s.pool!.managerAddress!,
      }));
  }, [isOwnProfile, rawProfileSeats]);

  const { balances: profileLiveBalances } = useLiveBalances(liveSeatRefs);

  const seats = useMemo(() => {
    if (profileLiveBalances.size === 0) return rawProfileSeats;
    return rawProfileSeats.map((s: any) => {
      const poolId = s.pool?.id || s.poolId || '';
      const live = getLiveBalance(profileLiveBalances, poolId, s.seatIndex);
      if (live === undefined) return s;
      return { ...s, balance: live.toString() };
    });
  }, [rawProfileSeats, profileLiveBalances]);

  const v2YieldTokenAddrs = useMemo(() => {
    const addrs = new Set<string>();
    for (const seat of seats) {
      if (seat.pool?.isV2Grid && seat.pool?.v2YieldToken) {
        addrs.add(effectiveTokenAddress(seat.pool.v2YieldToken));
      }
    }
    return Array.from(addrs);
  }, [seats]);

  const { prices: tokenPrices } = useTokenPrices(v2YieldTokenAddrs);

  const v1PoolIdsWithRewards = useMemo(() => {
    const ids = new Set<string>();
    for (const seat of seats) {
      if (!seat.pool?.isV2Grid && seat.pool?.id && BigInt(seat.pool.totalRewardsEth || '0') > 0n) {
        ids.add(seat.pool.id);
      }
    }
    return Array.from(ids);
  }, [seats]);

  const { data: rewards7dMap } = useQuery({
    queryKey: ['profile-rewards-7d', v1PoolIdsWithRewards],
    queryFn: async () => {
      const map = new Map<string, string>();
      await Promise.all(
        v1PoolIdsWithRewards.map(async (poolId) => {
          try {
            const res = await fetch(`/api/pools/${poolId}/rewards?window=7d`);
            if (!res.ok) return;
            const json = await res.json();
            if (json.data?.totalRewardsEth7d) map.set(poolId, json.data.totalRewardsEth7d);
          } catch {
            /* skip */
          }
        }),
      );
      return map;
    },
    enabled: v1PoolIdsWithRewards.length > 0,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const groupedSeats = useMemo(
    () => groupSeatsByCoin(seats, liveAvgEarningsUsd, ethPrice, tokenPrices, rewards7dMap),
    [seats, liveAvgEarningsUsd, ethPrice, tokenPrices, rewards7dMap],
  );
  const { priceUsd: liveTakeoverPrice } = useTakeoverPrice();
  const takeoverPriceUsd = liveTakeoverPrice || profileData?.takeoverPriceUsd || 0;
  const summary = useMemo(
    () => calculateSummary(groupedSeats, takeoverPriceUsd),
    [groupedSeats, takeoverPriceUsd],
  );

  const copyAddress = () => {
    navigator.clipboard.writeText(address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openAvatarDialog = useCallback(() => {
    setAvatarDialogOpen(true);
  }, []);

  // "View" on a tile/board row in the profile dialog. The legacy
  // side-sheet preview was retired — instead we close the dialog and
  // route the user to the board's own page (or to its classic-app
  // surface for the legacy boardroom + V1 coin pools that never got an
  // in-app detail page). When the user is already on the destination
  // board we just dismiss the dialog so the click feels like a no-op
  // navigation rather than a jarring re-push of the same route.
  // Modifier-clicks are handled at the call site (Link's default
  // open-in-new-tab behaviour is preserved by skipping `preventDefault`),
  // so this callback only ever runs for plain primary clicks.
  const openGridFromTiles = useCallback(
    (args: { poolId: string; isV2: boolean; memecoinAddress?: string }) => {
      if (args.poolId === HV_TAKEOVER_GRID_ID) {
        closeProfileModal();
        window.location.assign(classicAppUrl('/boardroom'));
        return;
      }
      if (!args.isV2) {
        closeProfileModal();
        window.location.assign(
          classicAppUrl(`/coin/${args.memecoinAddress || args.poolId}`),
        );
        return;
      }
      const href = gridHref(args.poolId);
      closeProfileModal();
      if (pathname !== href) router.push(href);
    },
    [closeProfileModal, pathname, router],
  );

  if (!address.startsWith('0x')) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">Invalid profile address.</div>
    );
  }

  if (portfolioLoading && !embedded) {
    return (
      <div className="container max-w-4xl mx-auto py-6 space-y-6">
        <div className="flex items-start gap-6">
          <Skeleton className="h-20 w-20 rounded-lg" />
          <div className="space-y-2 flex-1">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-lg" />
          ))}
        </div>
        <Skeleton className="h-10 w-64" />
        <div className="space-y-3">
          {[...Array(2)].map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  const displayName = profileDisplayName || `${address.slice(0, 8)}...${address.slice(-6)}`;
  const avatarUrl = profileAvatarUrl || generateGridAvatarUrl(address, 80);

  const hudSnapshot = useMemo(
    () => buildHudSnapshot(summary, user, ethPrice),
    [summary, user, ethPrice],
  );

  const tilesTab =
    portfolioLoading && embedded ? (
      <div className="space-y-2">
        {[0, 1, 2].map((i) => (
          <Skeleton
            key={i}
            className="h-[76px] w-full rounded-[4px] border border-amber-500/10 bg-amber-950/25"
          />
        ))}
      </div>
    ) : (
      <ProfileTilesList
        groups={groupedSeats}
        takeoverPriceUsd={takeoverPriceUsd}
        onOpenGrid={openGridFromTiles}
      />
    );

  const gridsTab = (
    <>
      {isGridsLoading ? (
        <div className="space-y-3 w-full">
          {[...Array(3)].map((_, i) => (
            <Skeleton
              key={i}
              className="h-14 w-full rounded-[4px] border border-amber-500/10 bg-amber-950/30"
            />
          ))}
        </div>
      ) : creatorGrids.length > 0 ? (
        creatorGrids.map((grid) => (
          <CreatorGridCard
            key={grid.id}
            grid={grid}
            isOwnProfile={isOwnProfile}
            readOnly
            onNavigate={() => openGridFromTiles({ poolId: grid.id, isV2: true })}
          />
        ))
      ) : (
        <div className="rounded-[4px] border border-dashed border-amber-500/20 bg-black/20 py-12 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 rounded-xl bg-amber-950/40 flex items-center justify-center border border-amber-500/15">
              <Plus className="h-6 w-6 text-zinc-500" />
            </div>
          </div>
          <p className="text-zinc-400 mb-1 text-sm">No boards created yet</p>
          <p className="text-xs text-zinc-500 mb-4">Create a board to earn from tile trading and tax revenue.</p>
          <Button asChild variant="outline" size="sm" className="border-amber-500/30 text-amber-100">
            <Link href="/create">Create a board</Link>
          </Button>
        </div>
      )}
    </>
  );

  return (
    <div
      className={cn(
        embedded ? 'w-full max-w-none py-0' : 'container max-w-[1000px] mx-auto py-6 px-3',
      )}
    >
      <ProfileHud
        variant={embedded ? 'embedded' : 'page'}
        address={address}
        displayName={displayName}
        avatarUrl={avatarUrl}
        level={userLevel}
        isOwnProfile={isOwnProfile}
        onClose={embedded ? closeProfileModal : undefined}
        copied={copied}
        onCopyAddress={copyAddress}
        userProfile={hudUserProfile}
        linkTwitter={linkTwitter}
        snapshot={hudSnapshot}
        tilesTab={tilesTab}
        gridsTab={gridsTab}
        defaultTab="activity"
        onEditAvatar={isOwnProfile ? openAvatarDialog : undefined}
        portfolioLoading={portfolioLoading && embedded}
      />

      <Dialog open={avatarDialogOpen} onOpenChange={setAvatarDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Update Profile Picture</DialogTitle>
            <DialogDescription>
              {privyUser?.twitter
                ? 'Your profile image is synced from your connected X account.'
                : 'Update your profile image by connecting X.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex justify-center">
              <AvatarWithFlair avatarUrl={avatarUrl} address={address} level={userLevel} size="xl" />
            </div>

            {privyUser?.twitter ? (
              <p className="text-sm text-center text-muted-foreground">
                Connected as{' '}
                <span className="font-medium text-foreground">@{privyUser.twitter.username}</span>. Your X profile
                image will sync automatically.
              </p>
            ) : (
              <Button
                onClick={() => {
                  setAvatarDialogOpen(false);
                  linkTwitter();
                }}
                className="w-full"
              >
                <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
                Connect X
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
